import { Link } from 'react-router-dom';
import JsonLd from '@/components/feature/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://basetaosheet.wtf';

interface ArticleSection {
  id: string;
  heading: string;
  paragraphs?: string[];
  subSections?: { heading: string; paragraphs: string[] }[];
  featureCards?: { title: string; description: string }[];
  reviewCards?: { title: string; description: string }[];
  comparisonRows?: { platform: string; description: string }[];
  tipCards?: { title: string; description: string }[];
  faqs?: { question: string; answer: string }[];
}

interface ArticleContent {
  title: string;
  metaDescription: string;
  metaKeywords: string;
  publishedDate: string;
  sections: ArticleSection[];
}

function buildArticleSchema(
  content: ArticleContent,
  breadcrumbLabel: string,
  headerImage: string,
  slug: string,
) {
  const articleUrl = `${siteUrl}${slug}`;

  const faqSections = content.sections.filter((s) => s.faqs && s.faqs.length > 0);
  const hasFAQ = faqSections.length > 0;

  const articleSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: content.title,
    description: content.metaDescription,
    image: headerImage,
    datePublished: content.publishedDate,
    dateModified: content.publishedDate,
    author: {
      '@type': 'Organization',
      name: 'Basetao Spreadsheet Hub',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Basetao Spreadsheet Hub',
      url: siteUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: breadcrumbLabel,
        item: articleUrl,
      },
    ],
  };

  const schemas: Record<string, unknown>[] = [articleSchema, breadcrumbSchema];

  if (hasFAQ) {
    const allFaqs = faqSections.flatMap((s) => s.faqs || []);
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: allFaqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  return schemas;
}

export function ArticleSectionRenderer({ section }: { section: ArticleSection }) {
  return (
    <section id={section.id} className="mb-10 md:mb-14 scroll-mt-24">
      <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
        {section.heading}
      </h2>

      {section.paragraphs?.map((p, i) => (
        <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
          {p}
        </p>
      ))}

      {section.subSections?.map((sub) => (
        <div key={sub.heading} className="mb-4">
          <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-800 mb-2">
            {sub.heading}
          </h3>
          {sub.paragraphs.map((p, i) => (
            <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
              {p}
            </p>
          ))}
        </div>
      ))}

      {section.featureCards && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {section.featureCards.map((card) => (
            <div
              key={card.title}
              className="bg-background-100 border border-background-200/60 rounded-lg p-4 md:p-5"
            >
              <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2">
                {card.title}
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                {card.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {section.reviewCards && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {section.reviewCards.map((card) => (
            <div
              key={card.title}
              className="bg-background-100 border border-accent-200/40 rounded-lg p-4 md:p-5"
            >
              <h3 className="font-heading text-sm md:text-base font-semibold text-accent-700 mb-2">
                {card.title}
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                {card.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {section.comparisonRows && (
        <div className="space-y-3 mt-4">
          {section.comparisonRows.map((row) => (
            <div
              key={row.platform}
              className="bg-background-100 border border-background-200/60 rounded-lg p-4 md:p-5"
            >
              <h3 className="font-heading text-sm md:text-base font-semibold text-primary-600 mb-1">
                Basetao vs {row.platform}
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                {row.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {section.tipCards && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          {section.tipCards.map((tip) => (
            <div
              key={tip.title}
              className="bg-secondary-50 border border-secondary-200/60 rounded-lg p-4 md:p-5"
            >
              <h3 className="font-heading text-sm md:text-base font-semibold text-secondary-800 mb-2">
                {tip.title}
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                {tip.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {section.faqs && (
        <div className="space-y-3 mt-4">
          {section.faqs.map((faq) => (
            <details
              key={faq.question}
              className="bg-background-100 border border-background-200/60 rounded-lg group"
            >
              <summary className="p-4 md:p-5 cursor-pointer flex items-center justify-between text-sm md:text-base font-medium text-foreground-900">
                {faq.question}
                <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform">
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </summary>
              <div className="px-4 md:px-5 pb-4 md:pb-5">
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}

export default function ArticleRenderer({
  content,
  breadcrumbLabel,
  headerImage,
  articleSlug,
}: {
  content: ArticleContent;
  breadcrumbLabel: string;
  headerImage: string;
  articleSlug: string;
}) {
  const schemas = buildArticleSchema(content, breadcrumbLabel, headerImage, articleSlug);

  return (
    <article className="pt-24 pb-14 md:pb-20">
      {schemas.map((schema, i) => (
        <JsonLd key={i} data={schema} />
      ))}

      <header className="max-w-3xl mx-auto px-4 md:px-6 mb-10">
        <nav className="mb-6" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-500 transition-colors">
                Home
              </Link>
            </li>
            <li className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </li>
            <li className="text-foreground-700 font-medium">
              {breadcrumbLabel}
            </li>
          </ol>
        </nav>

        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
          {content.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500 mb-6">
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line"></i>
            </span>
            Published: {content.publishedDate}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </span>
            12 min read
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-price-tag-3-line"></i>
            </span>
            Basetao Spreadsheet
          </span>
        </div>

        <div className="w-full h-48 md:h-64 rounded-lg overflow-hidden mb-8">
          <img
            src={headerImage}
            alt={content.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <nav className="bg-background-100 rounded-lg border border-background-200/60 p-5 md:p-6 mb-8">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-3">
            Table of Contents
          </h2>
          <ol className="flex flex-col gap-1.5">
            {content.sections.map((section, i) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="text-xs md:text-sm text-foreground-600 hover:text-primary-500 transition-colors flex items-center gap-2"
                >
                  <span className="text-foreground-400 font-medium text-xs w-5 text-right">
                    {i + 1}.
                  </span>
                  {section.heading}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-6">
        {content.sections.map((section) => (
          <ArticleSectionRenderer key={section.id} section={section} />
        ))}
      </div>
    </article>
  );
}